/**
 * Reprise d'un exercice après un paiement (Stripe recharge l'app).
 *
 * Avant de partir payer, on mémorise la tentative en cours (si elle n'est pas
 * terminée) et l'index du pas où reprendre (le 1er pas verrouillé). Au retour
 * (`?pass_success=1`), le player reprend À LA QUESTION SUIVANTE : réponses déjà
 * données relues en base, ou nouvelle tentative si la précédente a été validée
 * (XP déjà créditée) — jamais de retour à la question 1.
 */

const ATTEMPT_KEY = (exerciseId: string) => `exercise-attempt-${exerciseId}`;
const STEP_KEY = (exerciseId: string) => `exercise-resume-step-${exerciseId}`;

export interface ExerciseResume {
  /** Tentative à reprendre (`in_progress`) ; null = en démarrer une nouvelle. */
  attemptId: string | null;
  /** Pas où reprendre ; null = 1er pas non répondu de la tentative. */
  stepIdx: number | null;
}

export function rememberResume(exerciseId: string, resume: ExerciseResume): void {
  try {
    if (resume.attemptId) sessionStorage.setItem(ATTEMPT_KEY(exerciseId), resume.attemptId);
    else sessionStorage.removeItem(ATTEMPT_KEY(exerciseId));
    if (resume.stepIdx !== null)
      sessionStorage.setItem(STEP_KEY(exerciseId), String(resume.stepIdx));
    else sessionStorage.removeItem(STEP_KEY(exerciseId));
  } catch {
    /* stockage indisponible : reprise au début */
  }
}

/** Lit ET efface la reprise mémorisée (null si rien n'a été mémorisé). */
export function takeResume(exerciseId: string): ExerciseResume | null {
  try {
    const attemptId = sessionStorage.getItem(ATTEMPT_KEY(exerciseId));
    const rawStep = sessionStorage.getItem(STEP_KEY(exerciseId));
    sessionStorage.removeItem(ATTEMPT_KEY(exerciseId));
    sessionStorage.removeItem(STEP_KEY(exerciseId));
    const step = rawStep === null ? NaN : Number(rawStep);
    const stepIdx = Number.isInteger(step) && step >= 0 ? step : null;
    if (!attemptId && stepIdx === null) return null;
    return { attemptId: attemptId || null, stepIdx };
  } catch {
    return null;
  }
}

/** Pas où reprendre : `preferred` s'il est fourni, sinon le 1er pas QCM non répondu. */
export function resumeStepIndex(
  steps: { checkpointId: string | null }[],
  answered: ReadonlySet<string>,
  preferred: number | null,
): number {
  if (steps.length === 0) return 0;
  if (preferred !== null) return Math.min(Math.max(0, preferred), steps.length - 1);
  const i = steps.findIndex((s) => s.checkpointId !== null && !answered.has(s.checkpointId));
  return i === -1 ? steps.length - 1 : i;
}

export interface ResumedAnswer {
  correctionId: string;
  chosen_id: string;
  chosen_ids: string[];
  was_correct: boolean;
}

/** Lignes `attempt_steps` (V2) → réponses du player, indexées par checkpoint. */
export function answersFromAttemptSteps(
  rows: {
    block_id: string | null;
    chosen_block_id: string | null;
    chosen_block_ids: string[] | null;
    was_correct: boolean | null;
  }[],
): Record<string, ResumedAnswer> {
  const out: Record<string, ResumedAnswer> = {};
  for (const r of rows) {
    if (!r.block_id) continue;
    const ids = r.chosen_block_ids?.length
      ? r.chosen_block_ids
      : r.chosen_block_id
        ? [r.chosen_block_id]
        : [];
    out[r.block_id] = {
      correctionId: r.block_id,
      chosen_id: ids[0] ?? '',
      chosen_ids: ids,
      was_correct: r.was_correct === true,
    };
  }
  return out;
}

export interface AutoResumePlan {
  /** Pas où reprendre (1re question sans réponse, sinon le dernier pas). */
  stepIdx: number;
  /** Toutes les questions sont répondues : on rouvre directement le bilan. */
  atEnd: boolean;
}

/**
 * Rechargement de la page / retour sur un exercice EN COURS (tentative
 * `in_progress`, XP pas encore validée) : on reprend cette tentative au lieu de
 * recommencer à la question 1. Rien à reprendre (aucune réponse) → null.
 */
export function planAutoResume(
  steps: { checkpointId: string | null }[],
  answered: ReadonlySet<string>,
): AutoResumePlan | null {
  if (steps.length === 0 || answered.size === 0) return null;
  const first = steps.findIndex((s) => s.checkpointId !== null && !answered.has(s.checkpointId));
  if (first === -1) return { stepIdx: steps.length - 1, atEnd: true };
  return { stepIdx: first, atEnd: false };
}

/** Temps déjà passé sur la tentative (somme des temps de réponse enregistrés). */
export function elapsedFromAttemptSteps(rows: { time_ms?: number | null }[]): number {
  return rows.reduce((s, r) => s + Math.max(0, Number(r.time_ms) || 0), 0);
}
